"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, RefreshCw, TrendingUp, Flame } from "lucide-react";
import { secureGet } from "@/lib/security/fetch-client";

interface HotspotData {
  theme: string;
  analysis: string;
  trendingTopics: Array<{ name: string; reason: string }>;
  cached?: boolean;
}

export function HotspotInsight() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<HotspotData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchInsight();
  }, []);

  const fetchInsight = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await secureGet<HotspotData>("/api/ai/hotspot");
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "获取热点解读失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 border-purple-200 dark:border-purple-800">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-purple-500" />
            AI 热点解读
          </CardTitle>
          <div className="flex items-center gap-2">
            {data?.cached && (
              <Badge variant="secondary" className="text-xs">
                已缓存
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchInsight}
              disabled={loading}
              className="h-8 w-8 p-0"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <div className="flex gap-2 mt-4">
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-6 w-16" />
            </div>
          </div>
        ) : error ? (
          <div className="text-sm text-red-500">{error}</div>
        ) : data ? (
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-orange-500" />
                <span className="font-medium text-sm">今日热点主题</span>
              </div>
              <p className="text-sm text-muted-foreground">{data.theme}</p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <Flame className="h-4 w-4 text-red-500" />
                <span className="font-medium text-sm">趋势分析</span>
              </div>
              <p className="text-sm text-muted-foreground">{data.analysis}</p>
            </div>

            {data.trendingTopics.length > 0 && (
              <div>
                <span className="font-medium text-sm mb-2 block">值得关注</span>
                <div className="flex flex-wrap gap-2">
                  {data.trendingTopics.map((topic, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className="bg-white/50 dark:bg-black/20"
                    >
                      {topic.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
